import React, { FC, useState } from "react";
import { GroupBlockEntityType } from "../../interfaces/entity";
import { getInLanguageOrDefault } from "../../../common/utilities/language";
import { useTranslation } from "react-i18next";
import { Popover } from "antd";
import { useAppDispatch, useAppSelector } from "../../../app";
import linkedEntitiesSelector from "../../selectors/linkedEntitiesSelector";
import "./EntityTitle.scss";
import { getAccessibleTextColor } from "../../../common/utilities/color";
import { shiftAnnotationEntitiesWithAnalytics } from "../../../annotation/store/annotationSlice";
import { Entity } from "../../../annotator/interfaces/entity";
import useAnnotationNormalization from "../../hooks/useAnnotationNormalization";
import { Annotation } from "../../../annotator/interfaces/annotation";
import { mapToEntityAnnotationDto } from "../../../annotation/utils/annotationMappers";
import EntityConversionTitle from "./EntityConversionTitle";
import { EntityLink } from "../../interfaces/conversion";

type Props = {
  disabled: boolean;
  groupBlockEntityType: GroupBlockEntityType;
  entityTypes: Array<GroupBlockEntityType> | null;
};

const EntityTitle: FC<Props> = ({
  disabled,
  groupBlockEntityType,
  entityTypes,
}) => {
  const { entityType } = groupBlockEntityType;
  const { translations } = entityType;

  const [open, setOpen] = useState(false);

  const linkedEntities = useAppSelector((state) =>
    linkedEntitiesSelector(state, { groupBlockEntityType }),
  );

  const {
    t,
    i18n: { language },
  } = useTranslation("detail");

  const dispatch = useAppDispatch();

  const { normalizeEntityAnnotationsInBulk } = useAnnotationNormalization();

  const textContent = `${getInLanguageOrDefault(translations, language)} ${
    groupBlockEntityType.required ? "*" : ""
  }`.trim();

  const linkedEntityNames = linkedEntities.map((e) =>
    getInLanguageOrDefault(e.entityType.translations, language),
  );
  const entityLink: EntityLink | undefined = linkedEntityNames.length
    ? {
        direction: groupBlockEntityType.parentId ? "from" : "recalculates",
        names: linkedEntityNames,
      }
    : undefined;

  if (linkedEntities.length) {
    return (
      <div className="entity-title__container">
        <span className="entity-title__label">
          <EntityConversionTitle
            entityName={getInLanguageOrDefault(translations, language)}
            fallbackTooltip={textContent}
            entityTypeId={entityType.id}
            entityNormalizations={entityType.entityNormalizations}
            entityLink={entityLink}
          >
            <span className="entity-title__text">{textContent}</span>
          </EntityConversionTitle>
        </span>
      </div>
    );
  }

  if (entityTypes?.length) {
    return (
      <div className="entity-title__container">
        <span className="entity-title__label">
          <EntityConversionTitle
            entityName={getInLanguageOrDefault(translations, language)}
            fallbackTooltip={textContent}
            entityTypeId={entityType.id}
            entityNormalizations={entityType.entityNormalizations}
          >
            <span className="entity-title__text">{textContent}</span>
          </EntityConversionTitle>
        </span>
        <Popover
          content={
            <div className="menu__container">
              <hr />
              <ul>
                {entityTypes
                  .filter((e) => e.entityType.id !== entityType.id)
                  .map((e) => {
                    const label = getInLanguageOrDefault(
                      e.entityType.translations,
                      language,
                    );

                    const targetEntity: Entity = {
                      id: e.entityType.id,
                      name: label,
                      color: e.color!,
                      entityType: "NER",
                      multipleGroupBlocks: true,
                      entityNormalizations: e.entityType.entityNormalizations,
                      index: e.index,
                    };

                    return (
                      <li
                        key={e.id}
                        onClick={async () => {
                          if (disabled) {
                            return;
                          }

                          const result = await dispatch(
                            shiftAnnotationEntitiesWithAnalytics({
                              sourceEntityId: entityType.id,
                              targetEntity,
                            }),
                          );

                          const annotationsToNormalize:
                            Annotation[] | undefined = result.payload as
                            Annotation[] | undefined;

                          if (
                            annotationsToNormalize &&
                            annotationsToNormalize.length > 0
                          ) {
                            normalizeEntityAnnotationsInBulk(
                              annotationsToNormalize.map((annotation) => {
                                return mapToEntityAnnotationDto(
                                  annotation.id,
                                  annotation.values.join(" "),
                                  annotation.index || 1,
                                  annotation.entity.entityNormalizations ?? [],
                                  annotation.entity.id,
                                  annotation.multipleGroupBlocks,
                                );
                              }),
                              true,
                            );
                          }

                          setOpen(false);
                        }}
                      >
                        <span
                          style={{
                            backgroundColor: e.color,
                            color: getAccessibleTextColor(e.color || ""),
                          }}
                          className="menu__simple-hotkey"
                        >
                          {e.hotkey?.toUpperCase()}
                        </span>
                        <span>{label}</span>
                      </li>
                    );
                  })}
              </ul>
            </div>
          }
          title={t("popover.title")}
          trigger="click"
          open={open}
          onOpenChange={setOpen}
        >
          <span
            className="bi bi-arrow-left-right entity-title__menu-icon"
            style={{ display: disabled ? "none" : "inline-block" }}
          />
        </Popover>
      </div>
    );
  }

  return (
    <div className="entity-title__container">
      <span className="entity-title__label">
        <EntityConversionTitle
          entityName={getInLanguageOrDefault(translations, language)}
          fallbackTooltip={textContent}
          entityTypeId={entityType.id}
          entityNormalizations={entityType.entityNormalizations}
        >
          <span className="entity-title__text">{textContent}</span>
        </EntityConversionTitle>
      </span>
    </div>
  );
};

export default EntityTitle;
